<template>
  <div class="modal-content">
    <div class="column">
      <div class="image-container">
        <camera-button :disabled="false" @update-image-src="handleImageSrc" />
        <div v-if="selectedImage" style="position: relative;">
          <img
            :src="selectedImage"
            alt="Selected Image"
            @load="onImageLoad"
            ref="image"
            class="selected-image"
          />
          <canvas ref="canvas" class="overlay-canvas"></canvas>
          <canvas ref="imageCanvas" style="display: none;"></canvas>
        </div>
      </div>
    </div>
    <div class="column">
      <div v-if="boundingBoxes">
        <h4>Textos reconhecidos:</h4>
      </div>
      <div v-if="boundingBoxes" class="text-blocks-container">
        <div
          class="text-block"
          :class="{ selected: selectedTextIndex.includes(index) }"
          v-for="(block, index) in boundingBoxes"
          :key="index"
          @click="selectTextBlock(block, index)"
        >
          {{ block.text }}
        </div>
      </div>
      <div class="confirm-container">
        <label for="selected-text">Texto Selecionado:</label>
        <b-form-input
          id="selected-text"
          :value="concatenatedSelectedTexts"
          @input="concatenatedSelectedTexts = $event"
        ></b-form-input>
        <div class="button-container">
          <b-button
            size="sm"
            variant="green"
            @click="emitEditedText"
            style="margin-right: 10px;"
            >Confirmar</b-button
          >
          <b-button size="sm" @click="cancel">Cancelar</b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CameraButton from "@/components_rms/CameraButton.vue";
import {
  recognizeText,
  applyFormattingRules,
  extractInformation
} from "./OcrService";

export default {
  name: "OcrModalContent",
  components: {
    CameraButton
  },
  props: {
    checklistValue: Object,
    initialImage: String,
    previousRecognizedText: Array,
    previewText: String
  },
  data() {
    return {
      imgSource: null,
      selectedImage: null,
      recognizedText: null,
      file: null,
      boundingBoxes: [],
      selectedTexts: [],
      selectedTextIndex: [],
      originalSelectedText: []
    };
  },
  mounted() {
    if (this.initialImage) {
      this.imgSource = this.initialImage;
      this.selectedImage = this.initialImage;
      this.boundingBoxes = this.previousRecognizedText;
      this.selectPreviewText();
    }
  },
  computed: {
    concatenatedSelectedTexts: {
      get() {
        return this.selectedTexts.join(" ");
      },
      set(value) {
        this.selectedTexts = value.split(" ");
      }
    }
  },
  methods: {
    resetVariables() {
      this.recognizedText = null;
      this.selectedTexts = [];
      this.boundingBoxes = [];
      this.selectedTextIndex = [];
      this.originalSelectedText = [];
    },
    selectPreviewText() {
      if (!this.previewText) return;

      const sortedBoundingBoxes = this.boundingBoxes
        .slice()
        .sort((a, b) => b.text.length - a.text.length);

      for (let i = 0; i < sortedBoundingBoxes.length; i++) {
        if (sortedBoundingBoxes[i].text.trim() === this.previewText.trim()) {
          const index = this.boundingBoxes.indexOf(sortedBoundingBoxes[i]);
          this.selectTextBlock(this.boundingBoxes[index], index);
          break;
        }
      }
    },
    selectTextBlock(block, index) {
      if (this.checklistValue.ocrType === 1) {
        const selectedIndex = this.selectedTextIndex.indexOf(index);
        if (selectedIndex === -1) {
          this.selectedTexts.push(block.text);
          this.selectedTextIndex.push(index);
          this.originalSelectedText.push(block.text);
        } else {
          this.selectedTexts.splice(selectedIndex, 1);
          this.selectedTextIndex.splice(selectedIndex, 1);
          this.originalSelectedText.splice(selectedIndex, 1);
        }
      } else {
        this.selectedTexts = [block.text];
        this.selectedTextIndex = [index];
        this.originalSelectedText = [block.text];
      }
    },
    handleImageSrc(imageSrc) {
      this.imgSource = imageSrc;
      this.resetVariables();
      this.resizeImage(imageSrc);
    },
    resizeImage(imageSrc) {
      const img = new Image();
      img.src = imageSrc;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const MAX_DIMENSION = 1080;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_DIMENSION) {
            height *= MAX_DIMENSION / width;
            width = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width *= MAX_DIMENSION / height;
            height = MAX_DIMENSION;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        const resizedImageSrc = canvas.toDataURL("image/jpeg", 0.2);
        this.selectedImage = resizedImageSrc;
        if (this.boundingBoxes != this.previousRecognizedText) {
          fetch(resizedImageSrc)
            .then(res => res.blob())
            .then(blob => {
              this.file = new File([blob], "image.jpg", { type: "image/jpeg" });
              this.drawImageOnCanvas();
              this.recognizeText();
            });
        } else {
          fetch(resizedImageSrc)
            .then(res => res.blob())
            .then(blob => {
              this.file = new File([blob], "image.jpg", { type: "image/jpeg" });
              this.drawImageOnCanvas();
            });
        }
      };
    },
    onImageLoad() {
      this.drawImageOnCanvas();
      const canvas = this.$refs.canvas;
      const image = this.$refs.image;
      canvas.width = image.clientWidth;
      canvas.height = image.clientHeight;
      this.resizeImage(this.selectedImage);
    },
    drawImageOnCanvas() {
      const img = new Image();
      img.src = this.selectedImage;
      img.onload = () => {
        const imageCanvas = this.$refs.imageCanvas;
        const ctx = imageCanvas.getContext("2d");
        imageCanvas.width = img.width;
        imageCanvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);
      };
    },
    async recognizeText() {
      if (!this.file) return;

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result.split(",")[1];
        try {
          const response = await recognizeText(base64Image);
          this.processRecognitionResults(response);
        } catch (error) {
          console.error("Error recognizing text:", error);
        }
      };
      reader.readAsDataURL(this.file);
    },
    processRecognitionResults(response) {
      if (!response || !response.textAnnotations) {
        console.error("No textAnnotations found in the response");
        return;
      }

      this.recognizedText = response.fullTextAnnotation;
      this.boundingBoxes = applyFormattingRules(
        response,
        this.checklistValue.ocrType
      );

      if (this.boundingBoxes.length === 1) {
        this.selectTextBlock(this.boundingBoxes[0], 0);
      }
    },
    emitEditedText() {
      if (
        this.checklistValue.ocrScanLog &&
        this.checklistValue.ocrScanLog.AttachmentPath
      ) {
        this.$emit("remove-ocr-attachment", this.checklistValue.fieldId);
      }
      const concatenatedText = this.concatenatedSelectedTexts;
      const image = this.$refs.imageCanvas.toDataURL("image/jpeg", 0.2);
      const originalText = this.originalSelectedText.join(" ");
      let isEdited = 0;
      if (originalText !== concatenatedText) {
        isEdited = 1;
      } else {
        isEdited = 0;
      }
      this.checklistValue.value = concatenatedText;
      this.checklistValue.ocrScanLog = {
        id: 0,
        checklistValueId: this.checklistValue.id,
        attachmentPath: this.imgSource,
        editedValue: isEdited === 1 ? concatenatedText : null,
        scannedValue: originalText,
        isEdited: isEdited,
        ocrType: this.checklistValue.ocrType,
        fieldId: this.checklistValue.fieldId
      };
      this.$emit("text-edited", this.checklistValue);
      this.$emit("close-modal");
      this.$emit("img-loaded", image);
    },
    cancel() {
      this.$emit("close-modal");
    }
  }
};
</script>
<style scoped>
.confirm-container {
  width: 47%;
  bottom: 0;
  position: absolute;
  margin-top: 50px;
}
.button-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
.selected-image {
  max-width: 100%;
  max-height: 450px;
  display: block;
}
.overlay-canvas {
  position: absolute;
  top: 0;
  left: 0;
}
.image-container {
  float: left;
  margin-right: 20px;
}
.text-block {
  background-color: rgb(49, 49, 112);
  color: white;
  border-radius: 5px;
  text-align: center;
  margin-right: 10px;
}
.text-blocks-container {
  width: 100%;
  float: left;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 20px;
}
.text-blocks-container .text-block {
  cursor: pointer;
  margin-bottom: 5px;
}
.text-block:hover,
.text-block:focus {
  background-color: rgb(76, 76, 110);
  color: white;
}
.modal-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  height: 500px;
}
.selected {
  background-color: rgb(76, 76, 110);
  color: white;
}
</style>
